import { animate as motionAnimate2, timeline as motionTimeline2 } from "./js-vendor.min.js";
import { DialogElement } from "./js-dialog.js";

// Close button used in shadow DOM templates via `is="close-button"`
class CloseButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", () => {
      this.dispatchEvent(
        new CustomEvent("dialog:force-close", {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    });
  }
}

if (!window.customElements.get("close-button")) {
  window.customElements.define("close-button", CloseButton, { extends: "button" });
}

class Popover extends DialogElement {
  constructor() {
    super();
    const templateId = this.template;
    const templateEl = document.getElementById(templateId);

    if (!templateEl) {
      // Fail gracefully if the expected template is not in the DOM yet
      console.warn("[x-popover] template element not found", { templateId });
      return;
    }

    this.attachShadow({ mode: "open" }).appendChild(
      templateEl.content.cloneNode(true)
    );
  }

  connectedCallback() {
    super.connectedCallback?.();

    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot
      .querySelector('[part="overlay"]')
      ?.addEventListener("click", this.hide.bind(this), {
        signal: this._abortController?.signal,
      });

    this.controls.forEach((control) =>
      control.setAttribute("aria-haspopup", "dialog")
    );

    if (this.hasAttribute("close-on-listbox-select")) {
      this.addEventListener("listbox:select", this.hide, {
        signal: this._abortController?.signal,
      });
    }
  }

  get template() {
    return this.getAttribute("template") || "popover-default-template";
  }

  get shouldLock() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }

  get shouldAppendToBody() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }

  get anchor() {
    return {
      vertical: this.getAttribute("anchor-vertical") || "start",
      horizontal: this.getAttribute("anchor-horizontal") || "end",
    };
  }

  _showTransition(animate11 = true) {
    if (!this.shadowRoot) {
      return Promise.resolve();
    }

    let animationControls;
    const content = this.shadowRoot.querySelector('[part="content"]');
    const closeButton = this.shadowRoot.querySelector(
      '[part="outside-close-button"]'
    );

    this.style.display = "block";
    const isMobile = window.matchMedia("screen and (max-width: 999px)").matches;

    if (isMobile) {
      this.style.left = "0px";
      this.style.right = null;
      this.style.bottom = "0px";
      this.style.top = null;

      animationControls = motionTimeline2([
        [
          this,
          { opacity: [0, 1], visibility: ["hidden", "visible"] },
          { duration: 0.15 },
        ],
        [
          content,
          {
            clipPath: [
              "inset(100% 0 0 0 round 8px)",
              "inset(0 0 0 0 round 8px)",
            ],
          },
          { duration: 0.4, easing: [0.86, 0, 0.07, 1] },
        ],
        [content?.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }],
      ]);
    } else {
      const spacingBlockValue = "var(--popover-anchor-block-spacing)";
      const spacingInlineValue = "var(--popover-anchor-inline-spacing)";
      const anchor = this.anchor;

      this.style.left =
        anchor.horizontal === "start" ? spacingInlineValue : null;
      this.style.right =
        anchor.horizontal === "end" ? spacingInlineValue : null;

      if (anchor.vertical === "center") {
        this.style.top = `calc(50% - ${parseInt(this.clientHeight / 2, 10)}px)`;
        this.style.bottom = null;
      } else {
        this.style.top =
          anchor.vertical === "end"
            ? `calc(100% + ${spacingBlockValue})`
            : null;
        this.style.bottom =
          anchor.vertical === "start"
            ? `calc(100% + ${spacingBlockValue})`
            : null;
      }

      animationControls = motionTimeline2([
        [
          this,
          { opacity: [0, 1], visibility: ["hidden", "visible"] },
          { duration: 0.15 },
        ],
        [content, { clipPath: "none" }, { at: "<", duration: 0 }],
        [content?.children, { opacity: 1 }, { at: "<", duration: 0 }],
      ]);
    }

    if (!animationControls) {
      return Promise.resolve();
    }

    animate11 ? animationControls.play() : animationControls.finish();
    return animationControls.finished;
  }

  _hideTransition() {
    if (!this.shadowRoot) {
      this.style.display = "none";
      return Promise.resolve();
    }

    let animationControls;
    const isMobile = window.matchMedia("screen and (max-width: 999px)").matches;

    if (isMobile) {
      const content = this.shadowRoot.querySelector('[part="content"]');
      const closeButton = this.shadowRoot.querySelector(
        '[part="outside-close-button"]'
      );

      animationControls = motionTimeline2([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content?.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [
          content,
          { clipPath: [null, "inset(100% 0 0 0 round 8px)"] },
          { duration: 0.4, easing: [0.86, 0, 0.07, 1] },
        ],
        [
          this,
          { opacity: [null, 0], visibility: ["visible", "hidden"] },
          { duration: 0.15 },
        ],
      ]);
    } else {
      animationControls = motionAnimate2(
        this,
        { opacity: [null, 0], visibility: ["visible", "hidden"] },
        { duration: 0.15 }
      );
    }

    if (!animationControls) {
      this.style.display = "none";
      return Promise.resolve();
    }

    return animationControls.finished.then(() => {
      this.style.display = "none";
    });
  }
}

if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}
